import { fieldValue, isFavorite, type AnyEvent, type ItemRecord } from '@intentionalist/shared';
import { editItem } from './mutations.js';

/**
 * Picking an item out, from wherever it is being looked at (FR-3.12).
 *
 * Writes on the press rather than on a save, like a verdict and unlike
 * `protected` -- which is a form field, and so takes opening the item, ticking
 * a box and saving. That is three actions for a thing whose whole purpose is to
 * be quick, and it would also give one flag two ways to be set: a form holding
 * a stale value would undo a star pressed elsewhere the next time it saved.
 *
 * One component for the row, the grid tile and the item's page, so the three
 * cannot drift into meaning slightly different things. The page shows the word
 * as well, having room for it.
 */
export function FavoriteButton({
  item,
  onWrite,
  labelled = false,
}: {
  item: ItemRecord;
  onWrite: (events: AnyEvent[]) => void;
  /** Shows the word beside the star, where there is room for it. */
  labelled?: boolean;
}) {
  const on = isFavorite(item);
  const name = String(fieldValue(item, 'name') ?? 'this item');

  return (
    <button
      type="button"
      className={on ? 'favorite on' : 'favorite'}
      /*
       * A toggle, said in the way assistive technology reads as one, rather
       * than two buttons that swap places. `aria-label` names the item because
       * a row of identical stars is otherwise a list of "button, button".
       */
      aria-pressed={on}
      aria-label={on ? `Remove ${name} from favorites` : `Make ${name} a favorite`}
      onClick={() => onWrite(editItem(item.itemId, { favorite: !on }))}
    >
      {/*
        Filled or hollow, not merely a different colour (NFR-UX-4) -- the shape
        carries the state, and `aria-pressed` carries it again for anyone who
        cannot see either.
      */}
      <span aria-hidden="true">{on ? '★' : '☆'}</span>
      {labelled && <span>Favorite</span>}
    </button>
  );
}
